import React, { Fragment, useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { EiqSwitcher, EiqButton } from 'eiq-storybook';

import { CollapsibleBlock } from 'src/components/SurveyForm/constants';
import { QuestionRow } from 'src/components/JobDatasheet/QuestionRow';
import { ResultVerificationSectionPropTypes } from 'src/components/JobDatasheet/propTypes';
import { withJobContext } from 'src/utils/context/JobContext';
import Attachments from 'src/components/Attachments';
import { Popup } from 'src/components/Popup';
import JobFormStyle from 'src/components/JobForm/JobForm.styles';
import { getDependentQuestionsOnParentQuestion, editResultVerificationSection, canAddComment, isAttachmentDisabled } from 'src/components/JobDatasheet/JobDatasheetForm/utils';
import { notification } from 'src/components/Notification';
import { CommentsSection } from './CommentsSection/CommentsSection';
import s from './ResultVerificationSection.styles';
import ResultVerificationPagination from './ResultVerificationPagination';

const ResultVerificationSectionComponent = ({
  jobContext,
  activeQuestion,
  activeSection,
  currentResult,
  results,
  datasheetResultFamily,
  getResult,
  getResultAttachments,
  getResultExternalAttachments,
  getValidationErrors,
  isBlockSaveFailed,
  isBlockSaveSuccess,
  isSidebarExpanded,
  jobId,
  onDeletingAttachment,
  readonly,
  selectedQuestion,
  setResult,
  statistic,
  allRectifiedSections,
  allSections,
  allQuestionsLength,
  onChangeQuestion,
  resultWithOpenStatusCount,
  toggleOpenComment,
  setToggleOpenComment,
  currentUserId,
  isVerifier,
  isAdmin,
  isInfoExpanded,
  isReviewer,
  isExternalUser,
  userIsAuthorizedExternalSaqUser,
  storedResult,
  defaultResults,
  setIsSavingDatasheetCommentStatus,
}) => {
  const { t } = useTranslation();
  const [collapsedBlocks, setCollapsedBlocks] = useState(Object.values(CollapsibleBlock));
  const [isExtAttchmentCollapsed, setIsExtAttchmentCollapsed] = useState(true);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [updatedScroll, setUpdatedScroll] = useState(null);
  const commentSectionRef = useRef(null);  
  const [showModal, setShowModal] = useState(false);
  const [disableStatusButton, setDisableStatusButton] = useState(false);
  const [modalDetails, setModalDetails] = useState({
      title: t('IMPORT_RESULTS_WARNING_TITLE'),
      messageFirstLine: t('SAQ_RECITIFIED_ACTION_COMMENT_WARNING_MESSAGE'),
      buttonText: 'Confirm',
    }
  );
  
  const {
    job,
    user: {
      isAuditor,
      isReportReviewer,
      isInternalUser,
    },
    permissions: {
      isReportNotesAvailableToAuditor,
    },
  } = jobContext;

  const editVerificationSection = editResultVerificationSection(isVerifier, isReviewer, isAdmin, job.status);
  const canAddCommentSection = canAddComment(userIsAuthorizedExternalSaqUser, isVerifier, isReviewer, isAdmin, job.status);
  
  useEffect(() =>{
    setDisableStatusButton(false);
  }, [storedResult])

  useEffect(() => {
    setCollapsedBlocks(Object.values(CollapsibleBlock));
    setIsExtAttchmentCollapsed(getResultExternalAttachments(activeQuestion).length === 0);
    if (allRectifiedSections && activeQuestion) {
      let foundQuestionIndex = -1;
      const allQuestions = allRectifiedSections?.map(c => c).flat(1);
      foundQuestionIndex = allQuestions?.findIndex(ques => ques.id === activeQuestion?.id);
      setCurrentQuestion(foundQuestionIndex);
    } else if (!activeQuestion && (isInfoExpanded || isExternalUser)) {        
      const allQuestions = (allRectifiedSections || []).map(c => c).flat(1);
      onChangeQuestion(allQuestions[0]?.id);        
    }
  }, [activeQuestion, allRectifiedSections]);

  useEffect(() =>{    
    const allQuestions = (allRectifiedSections || []).map(c => c).flat(1);
    if (allRectifiedSections && (!activeQuestion && (isInfoExpanded || isExternalUser))) {  
      if(allQuestions.length > 0 ) onChangeQuestion(allQuestions[0]?.id); 
    }

    if(toggleOpenComment && currentResult?.commentStatus === 'resolved' && !isExternalUser && allQuestions.length !== 0) {     
      const actionQuestionNumber = activeQuestion?.questionNumber.split('.').map(Number);
      let nextQuestion = false;
      const nextQuestionItem = allQuestions.find((item) => {
        const questionNumber = item?.questionNumber.split('.').map(Number);
        const sameSectionQuestions = questionNumber[0] === actionQuestionNumber[0];
        if(sameSectionQuestions){
          nextQuestion = parseInt(questionNumber.join(''), 10) > parseInt(actionQuestionNumber.join(''), 10);
        }else if(questionNumber[0] > actionQuestionNumber[0]){
          nextQuestion = true;
        }
        return !!nextQuestion;
      });
      if(nextQuestionItem) onChangeQuestion(nextQuestionItem?.id);  
      else onChangeQuestion(allQuestions[allQuestions.length-1]?.id);        
    }
  },[allRectifiedSections])

  useEffect(() =>{
    if (!!isExternalUser && !!isInfoExpanded) {        
      const allQuestions = (allRectifiedSections || []).map(c => c).flat(1);
      onChangeQuestion(allQuestions[0]?.id);
    }
  }, [isInfoExpanded]);

  const outcomes =
    ((datasheetResultFamily || {}).currentVersion || {})
      .resultFamilyOutcomes || [];
  const positiveOutcomes =
    outcomes.filter(
      v => v.resultCategory.currentVersion.name === 'Positive Outcome',
    ) || [].sort((a, b) => a.order - b.order);
  const negativeOutcomes =
    outcomes.filter(
      v => v.resultCategory.currentVersion.name === 'Negative Outcome',
    ) || [].sort((a, b) => a.order - b.order);
  const savePositiveOutcomeAndRemoveComments = () => { 
    const {result} = getResult(activeQuestion);
    if(modalDetails?.field === 'validationResultId'){
      const outcome = modalDetails?.value;
      setResult(activeQuestion, 'validationResultId', outcome, false, true); 
      if(result){ 
        result.rectifiedValue = null;
        setResult(activeQuestion, 'result', result, false);
      }     
    }else if(modalDetails?.field === 'result'){
      setResult(modalDetails?.question, modalDetails?.field, modalDetails?.value, modalDetails?.shouldShowError, true);
      
    }
  } 
  
  const setQuestionResultId = outcome => {
    if (currentResult && currentResult.validationResultId !== outcome) {      
      const {result} = getResult(activeQuestion);
      if(positiveOutcomes.find(item => item.resultId === outcome)){
        let showModalFlag = false;
          const {datasheetQuestionId} = activeQuestion;
          const allQuestions = (allRectifiedSections || []).map(c => c).flat(1);
          const dependentQuestions = getDependentQuestionsOnParentQuestion((allQuestions || []).map(c => c).flat(1), datasheetQuestionId);
          
          for (let i = 0; i < dependentQuestions.length; i+=1){
            const dependenQuestion = dependentQuestions[i];   
            const dependentResultData = results.find(r => r?.datasheetQuestionId === dependenQuestion?.datasheetQuestionId);
            if(dependentResultData?.commentStatus === 'open'){    
              setModalDetails({
                title: t('IMPORT_RESULTS_WARNING_TITLE'),
                messageFirstLine: t('SAQ_RECITIFIED_ACTION_COMMENT_WARNING_MESSAGE'),
                buttonText: 'Confirm',
                field: 'validationResultId',
                value: outcome
              });
              setShowModal(true);
              showModalFlag = true;
              break;
            }
          }
        if(!showModalFlag){
          setResult(activeQuestion, 'validationResultId', outcome, false, true);
          if(result){ 
            result.rectifiedValue = null;
            setResult(activeQuestion, 'result', result, false);
          }
        }
      }else{
        setResult(activeQuestion, 'validationResultId', outcome, false, true);
        if(result){ 
          result.rectifiedValue = null;
          setResult(activeQuestion, 'result', result, false);
        }
      }
    }
  };

  /* if the active question is dependent dropdown.
  * When required, can be generalized for other types of dependent questions
  */
  const parentOfActiveQuestion = activeSection
    ?.questions
    .find(question => {
      return question.datasheetQuestion?.id ===
        activeQuestion?.datasheetQuestion?.currentVersion?.dependentDropdownSettings?.dependsOn;
    })
    || null;

  const onResultQuestionChange = questionNo => {
    const allQuestions = (allRectifiedSections || []).map(c => c).flat(1);
    onChangeQuestion(allQuestions[questionNo - 1]?.id);
  }

  const hasOpenComments = resultWithOpenStatusCount > 0;

  const handleCommentSectionScroll = () => {
    if (commentSectionRef.current) {
      const { scrollTop, scrollHeight: sHeight, clientHeight } = commentSectionRef.current;
      if (scrollTop + clientHeight >= sHeight - 5) {
        setUpdatedScroll(sHeight);
      }
    }
  };

  const setUpdatedCommentStatus = (updatedStatus) => {
    if (resultWithOpenStatusCount <= 1) {
      setIsSavingDatasheetCommentStatus(true);
    }
    setResult(activeQuestion, 'commentStatus', updatedStatus);
  }
  
  const setRectifiedResult = (question, field, value, shouldShowError, rectifiedFlag) =>{
    
    let allSectionQuestions = [];
    allSections.forEach(section => {
      if(section.children.length > 0){
        section.children.forEach(childSection => {
          allSectionQuestions = [...allSectionQuestions, ...childSection.questions.flat(1).map(c => c)];
        });
      }else{
        allSectionQuestions = [...allSectionQuestions, ...section.questions.flat(1).map(c => c)];
      }
      
    });
    
    const {datasheetQuestionId} = activeQuestion;
    const dependentQuestions = getDependentQuestionsOnParentQuestion((allSectionQuestions || []).map(c => c).flat(1), datasheetQuestionId);
    let confirmationModalFlag = false;
    for (let i = 0; i < dependentQuestions.length; i+=1){
      const dependenQuestion = dependentQuestions[i];   
      const dependenQuestionResultData = getResult(dependenQuestion);
      if(dependenQuestionResultData?.commentStatus === 'open'){   
        setModalDetails({
          title: t('IMPORT_RESULTS_WARNING_TITLE'),
          messageFirstLine: t('SAQ_RECITIFIED_ACTION_COMMENT_WARNING_MESSAGE'),
          buttonText: 'Confirm',
          question,
          field: 'result',
          value,
          shouldShowError
        });
        confirmationModalFlag = true;
        setShowModal(true);
        break;
      }
    }
    if(!confirmationModalFlag){      
      setResult(question, field, value, shouldShowError, rectifiedFlag);
    }
  }

  const saveEmptyResult = () =>{
    const field = 'validationResultId';
    setResult(activeQuestion, field, null, false, true);
  }
  
  return (<>
    <s.ResultVerificationSection isSidebarExpanded={isSidebarExpanded} reducedWidth={!isInternalUser}>
      <s.ResultVerificationSectionContent noCommentsAvailable={!hasOpenComments}>
        <s.OpenQuestionContentWrapper>
          {activeQuestion && !isExternalUser && (
            <EiqSwitcher
              className="switcherWrapper"
              isLabelLeft={false}
              label={t('QUESTION_WITH_OPEN_COMMENTS_ONLY')}
              onChange={setToggleOpenComment}
              id="toggle-open-comments"
            />
          )}
          { !isExternalUser && hasOpenComments && (
            <s.OpenCommentQuestionsCount>{resultWithOpenStatusCount}</s.OpenCommentQuestionsCount>
          )}
        </s.OpenQuestionContentWrapper>
        {((activeQuestion) && ((toggleOpenComment && hasOpenComments) || !toggleOpenComment)) && (
          <Fragment>
            <s.QuestionTextSection>
              <s.QuestionText>Question  {activeQuestion?.questionNumber}</s.QuestionText>
              <ResultVerificationPagination
                currentQuestion={currentQuestion} 
                totalQuestion={allQuestionsLength} 
                onPrevPage={onResultQuestionChange} 
                onNextPage={onResultQuestionChange} 
              />
            </s.QuestionTextSection>
            {!isExternalUser && 
              <s.OutcomesButtonWrapper>  
                <s.ResultVerificationSectionButtonContent disabled={!editVerificationSection}>

                   {positiveOutcomes.map(positiveOutcome => (
                    <EiqButton key={positiveOutcome.id} leftIcon={'check'} label={positiveOutcome.result.currentVersion.code} type={'type'} size={'btn--lg'} className={`accurateBtn ${currentResult?.validationResultId === positiveOutcome.resultId ? 'on' : ''}`} onClick={(e) => {e.preventDefault();setQuestionResultId(positiveOutcome.resultId)}} />
                  ))}
                  {negativeOutcomes.map(negativeOutcome => (
                    <EiqButton key={negativeOutcome.id} leftIcon={'xmark'} label={negativeOutcome.result.currentVersion.code} type={'type'} size={'btn--lg'} className={`inAccurate_Btn ${currentResult?.validationResultId === negativeOutcome.resultId ? 'on' : ''}`} onClick={(e) => {e.preventDefault();setQuestionResultId(negativeOutcome.resultId)}} />
                  ))}
                  {outcomes
                  .filter(v => !positiveOutcomes.some(p => p.resultId === v.resultId) && !negativeOutcomes.some(n => n.resultId === v.resultId)
                  )
                  .map(outcome => (<EiqButton key={outcome.id} label={outcome.result.currentVersion.code} type={'type'} size={'btn--lg'} className={`otherOutcome_Btn ${currentResult?.validationResultId === outcome.resultId ? 'on' : ''}`} onClick={(e) => {e.preventDefault();setQuestionResultId(outcome.resultId)}} />))}
                 </s.ResultVerificationSectionButtonContent>
              </s.OutcomesButtonWrapper>
            }
            <s.ScrollableContent ref={commentSectionRef}
                                 onScroll={handleCommentSectionScroll}
            >
              {!isExternalUser && negativeOutcomes.find(item => item.resultId === currentResult?.validationResultId) && (
                <s.QuestionRowWrapper disabled={!editVerificationSection}>
                  <QuestionRow
                    getResult={getResult}
                    getValidationErrors={getValidationErrors}
                    isBlockSaveFailed={isBlockSaveFailed}
                    isBlockSaveSuccess={isBlockSaveSuccess}
                    isRectifiedQuestion
                    parentOfActiveQuestion={parentOfActiveQuestion}
                    question={activeQuestion}
                    readonly={readonly}
                    result={currentResult?.result}
                    sectionOriginalIndex={activeSection.originalIndex}
                    selectedQuestion={selectedQuestion}
                    setResult={(question, field, value, shouldShowError) => setRectifiedResult(question, field, value, shouldShowError, true)}
                    statistic={statistic}
                  />
                </s.QuestionRowWrapper>
              )}
              { (currentResult?.id || isVerifier) && <s.CommentsSectionWrapper key={JSON.stringify(activeQuestion)} disabled={!canAddCommentSection}>
                <div>
                  <CommentsSection
                    key={JSON.stringify(activeQuestion?.id)}
                    canAddComment={canAddCommentSection}
                    id={'SAQcomments'}
                    currentUserId={currentUserId}
                    datasheetQuestionResultId={currentResult?.id}
                    selectedQuestion={selectedQuestion}
                    currentResult={currentResult}
                    updatedScroll={updatedScroll}
                    setUpdatedCommentStatus={setUpdatedCommentStatus}
                    status={currentResult?.commentStatus}
                    storedResult={storedResult}
                    defaultResults={defaultResults}
                    datasheetQuestionId={activeQuestion?.datasheetQuestionId}
                    setResult={saveEmptyResult}
                    isExternalUser={isExternalUser}
                    jobStatus={job.status}
                  />
                </div>
                { !isExternalUser && (<s.CommentStatusWrapper
                  id="comment-status-section"
                  isOpen={(currentResult?.commentStatus === 'open')}
                  onClick={() => {
                    const updatedStatus = (currentResult?.commentStatus === 'open') ? 'resolved' : 'open';
                    setDisableStatusButton(true);
                    setUpdatedCommentStatus(updatedStatus);
                  }}
                  disabled={!editVerificationSection || disableStatusButton} >
                  { currentResult?.commentStatus && <>
                    <s.StatusHeading>Status</s.StatusHeading>
                    <s.CommentStatus 
                      isOpen={(currentResult?.commentStatus === 'open')}>
                      {(currentResult?.commentStatus === 'open') ? t('OPEN') : t('RESOLVED')}
                    </s.CommentStatus>
                    </>
                  }
                  </s.CommentStatusWrapper>
                )}
              </s.CommentsSectionWrapper>
              }
            </s.ScrollableContent>
            <s.AttachmentsSection>
              <s.AttachmentsTitle>{t('ATTACHMENT')}</s.AttachmentsTitle>
              <Attachments
                    key={JSON.stringify(activeQuestion)}
                    attachmentIds={getResultAttachments(activeQuestion)}
                    disabled={isAttachmentDisabled(userIsAuthorizedExternalSaqUser, isAdmin, job.status) || readonly }
                    entity="attachments"
                    onChange={(_filename, _entity, id, meta) => {
                      setResult(
                        activeQuestion,
                        'attachments',
                        (currentResult?.attachments || []).concat({ attachmentId: id }),
                      );

                      const currentMeta = (
                        currentResult?.attachmentsMeta || []
                      ).map(({ __typename, ...rest }) => ({ ...rest }));

                      setResult(
                        activeQuestion,
                        'attachmentsMeta',
                        currentMeta.concat(meta),
                      );
                    }}
                    onDeleteFile = {readonly ? null : onDeletingAttachment}
                    pathPrefix={`job_${jobId}`}
                    style="cap"
                  />
            </s.AttachmentsSection>

          </Fragment>
        )}
        {toggleOpenComment && !hasOpenComments && 
        <s.NoQuestionOpenCommentSection>{t('NO_QUESTION_WITH_OPEN_COMMENT')}</s.NoQuestionOpenCommentSection>}
      </s.ResultVerificationSectionContent>
    </s.ResultVerificationSection>
    {
      showModal && (<JobFormStyle.SaqConfirmationModalWrapper>
        <Popup
        customTitle={<JobFormStyle.Title className="modal-title">{modalDetails?.title}</JobFormStyle.Title>}
        withOverflow
        isCloseHidden
        className="small-container"
        >
        <div className="saq-modal-body">
          <p>{modalDetails?.messageFirstLine}</p>
        </div>
        <JobFormStyle.SaqModalButtonsWrapper>
          <EiqButton 
            variant='btn--outline-link'
            label={t('CANCEL')}
            onClick={() =>{
              setShowModal(false);}}
          />
            <EiqButton
              label={modalDetails?.buttonText} 
              onClick={async (e) => {
                e.preventDefault();
                try {
                  savePositiveOutcomeAndRemoveComments();
                  setShowModal(false);
                } catch (error) {
                  setShowModal(false);
                  notification.error(t('SAVE_ERROR'));
                }
              }}
            />
        </JobFormStyle.SaqModalButtonsWrapper>
        </Popup>
      </JobFormStyle.SaqConfirmationModalWrapper>
      )}
    </>
  );
};

export const ResultVerificationSection = React.memo(withJobContext(ResultVerificationSectionComponent));

ResultVerificationSection.propTypes = ResultVerificationSectionPropTypes;
